import Handlebars from 'handlebars';
import { type LeadSchemaType } from '../models/Lead';
import { parsePhoneNumber } from 'react-phone-number-input';

// Twilio opt-out keywords that trigger blocking - per FCC ruling
// https://www.twilio.com/docs/proxy/opt-out-keywords
export const TWILIO_OPTOUT_KEYWORDS = [
  'STOP',
  'STOPALL',
  'UNSUBSCRIBE',
  'END',
  'QUIT',
  'CANCEL',
  'REVOKE',
  'OPTOUT',
  'ARRET',
  'ARRETE',
  'ARRETT',
] as const;

export function isTwilioOptOutMessage(body: string): boolean {
  const trimmed = (body ?? '').trim();
  if (!trimmed) return false;
  const upper = trimmed.toUpperCase();
  return TWILIO_OPTOUT_KEYWORDS.some(
    (kw) => upper === kw || upper.startsWith(kw + ' ')
  );
}

// Twilio opt-in keywords - user can receive messages again
export const TWILIO_OPTIN_KEYWORDS = ['UNSTOP', 'START', 'YES'] as const;

export function isTwilioOptInMessage(body: string): boolean {
  const trimmed = (body ?? '').trim();
  if (!trimmed) return false;
  const upper = trimmed.toUpperCase();
  return TWILIO_OPTIN_KEYWORDS.some(
    (kw) => upper === kw || upper.startsWith(kw + ' ')
  );
}

export function checkIsGSM(msg: string) {
  const gsmRegex =
    /^[\x0A\x0D\x20-\x7E\xA0\xA3\xA4\xA5\xA8\xA9\xAA\xAB\xAC\xAE\xB0\xB1\xB2\xB3\xB4\xB5\xB6\xB7\xB8\xB9\xBA\xBB\xBC\xBD\xBE\xBF]+$/;
  return !msg || gsmRegex.test(msg);
}

export function getMessageStats(msg: string) {
  if (!msg) {
    return { isGSM: true, charCount: 0, charPerSegment: 160, numberOfSegments: 1, maxChars: 160 };
  }

  const isGSM = checkIsGSM(msg);
  let charPerSegment = isGSM ? 160 : 70;
  let numberOfSegments = 1;

  const isMoreThanOneSegment = msg.length > charPerSegment;

  if (isMoreThanOneSegment) {
    charPerSegment = isGSM ? 153 : 67;
    numberOfSegments = Math.ceil(msg.length / charPerSegment);
  }

  const maxChars = numberOfSegments * charPerSegment;

  return { isGSM, charPerSegment, numberOfSegments, maxChars };
}

export function getCountryCode(phoneNumber: string) {
  try {
    return parsePhoneNumber(phoneNumber)?.country;
  } catch {
    return undefined;
  }
}

export const acceptedCountries = ['US', 'CA', 'GB', 'AU', 'GH', 'NG', 'ZA', 'BR', 'CO'];

export const acceptedCountriesMap = {
  US: 'United States',
  CA: 'Canada',
  GB: 'United Kingdom',
  AU: 'Australia',
  GH: 'Ghana',
  NG: 'Nigeria',
  ZA: 'South Africa',
  BR: 'Brazil',
  CO: 'Colombia',
};

export type CostByCountry = {
  [key: string]: number;
};

export const smsCostByCountry: CostByCountry = {
  US: 0.83 + 0.5, // United States
  CA: 0.83 + 0.87, // Canada
  GB: 5.24 + 0.5, // United Kingdom
  AU: 5.15 + 0.5, // Australia
  GH: 37.41 + 0.5, // Ghana
  NG: 38.68 + 0.5, // Nigeria
  ZA: 10.98 + 0.5, // South Africa
  BR: 5.99 + 0.5, // Brazil
  CO: 5.25 + 0.5, // Colombia
};

export const mmsCostByCountry: CostByCountry = {
  US: 2 + 1,
  CA: 2 + 1.7,
};

// Twilio inbound (receive) pricing per segment/message in cents
// https://www.twilio.com/en-us/sms/pricing
export const smsReceiveCostByCountry: CostByCountry = {
  US: 0.83 + 0.5,
  CA: 0.83 + 0.5,
  GB: 0.75 + 0.5,
  AU: 0.75 + 0.5,
  GH: 1 + 0.5,
  ZA: .75 + 0.5
};

export const mmsReceiveCostByCountry: CostByCountry = {
  US: 2 + 1,
  CA: 2 + 1.7,
  AU: 35 + 1,
};

export type SmsCampaignCostEstimateLead = Pick<
  LeadSchemaType,
  'phoneNumber' | 'firstName' | 'lastName'
>;

/** Estimated outbound SMS cost using Fanrank US/CA segment rates (template merged per lead like campaigns). For artist-owned Twilio this is informational only. */
export function estimateSmsCampaignFanrankCost({
  templateBody,
  leads,
}: {
  templateBody: string;
  leads: SmsCampaignCostEstimateLead[];
}): {
  totalCents: number;
  billedRecipientCount: number;
  skippedNoPhoneOrRate: number;
  totalSegmentsAcrossRecipients: number;
} {
  if (!templateBody.trim()) {
    return {
      totalCents: 0,
      billedRecipientCount: 0,
      skippedNoPhoneOrRate: leads.length,
      totalSegmentsAcrossRecipients: 0,
    };
  }

  const template = Handlebars.compile(templateBody);
  let totalCents = 0;
  let billedRecipientCount = 0;
  let skippedNoPhoneOrRate = 0;
  let totalSegmentsAcrossRecipients = 0;

  for (const lead of leads) {
    if (!lead.phoneNumber?.trim()) {
      skippedNoPhoneOrRate++;
      continue;
    }

    const countryCode = getCountryCode(lead.phoneNumber);
    if (!countryCode || (countryCode !== "US" && countryCode !== "CA")) {
      skippedNoPhoneOrRate++;
      continue;
    }

    const costPerSegment = smsCostByCountry[countryCode];

    if (!costPerSegment) {
      skippedNoPhoneOrRate++;
      continue;
    }

    const renderedBody = template({
      firstName: lead.firstName ?? '',
      lastName: lead.lastName ?? '',
    });
    const segments = getMessageStats(renderedBody).numberOfSegments;
    totalCents += segments * costPerSegment;
    billedRecipientCount++;
    totalSegmentsAcrossRecipients += segments;
  }

  return {
    totalCents,
    billedRecipientCount,
    skippedNoPhoneOrRate,
    totalSegmentsAcrossRecipients,
  };
}

export function getCostPerSegment({ leads }: { leads: LeadSchemaType[] }) {
  const countByCountry = new Map<string, number>();
  for (const lead of leads) {
    if (!lead.phoneNumber) {
      continue;
    }

    const countryCode = getCountryCode(lead.phoneNumber);
    if (!countryCode) {
      continue;
    }

    const count = countByCountry.get(countryCode) || 0;
    countByCountry.set(countryCode, count + 1);
  }

  const costPerSegment = Object.entries(smsCostByCountry).reduce(
    (total, [country, rate]) => total + (countByCountry.get(country) || 0) * rate,
    0,
  );

  return costPerSegment;
}
